import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useCallback, useEffect, useState } from "react";

import { logError } from "../../../utils/error";
import { TimeRange } from "../../../utils/timerange";
import PerformanceTable from "../../performance/components/PerformanceTable";
import { fetchProtocolMetrics, pickRandomUnique, ProtocolMetrics } from "../../performance/components/protocolQueries";
import { generateTrends } from "../../performance/utils/trend";

const SAMPLE_CALLS_COUNT = 20;

interface CallSamplesSectionProps {
  timeRange: TimeRange;
  timezone: string;
  protocol: string;
  user: string;
  permittedEmails: string[] | null;
  onCallClick: (interactionId: string, attribute?: string) => void;
}

const CallSamplesSection = ({
  timeRange,
  timezone,
  protocol,
  user,
  permittedEmails,
  onCallClick
}: CallSamplesSectionProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<ProtocolMetrics | null>(null);
  const [callsMode, setCallsMode] = useState<"recent" | "random">("recent");
  const [rawData, setRawData] = useState<ProtocolMetrics["enrichedScorecards"]>([]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchProtocolMetrics({ timeRange, timezone, protocol, user, permittedEmails });
        setMetrics(data);
        setRawData(data.enrichedScorecards);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An error occurred";
        logError("[CallSamplesSection] Error loading data", errorMessage);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [timeRange, timezone, protocol, user, permittedEmails]);

  const handleCallsModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: "recent" | "random" | null
  ) => {
    if (newMode !== null) {
      setCallsMode(newMode);
    }
  };

  const refreshRandomCalls = useCallback(() => {
    if (!rawData.length) {
      return;
    }

    const newRandomCalls = pickRandomUnique(rawData, SAMPLE_CALLS_COUNT);

    setMetrics((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        randomCalls: {
          calls: newRandomCalls,
          trends: generateTrends(newRandomCalls)
        }
      };
    });
  }, [rawData]);

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mt: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2, mt: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!metrics) {
    return (
      <Paper sx={{ p: 2, mt: 3 }}>
        <Alert severity="info">No call sample data available for the selected lens and time range.</Alert>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <PerformanceTable
              title="Top 5 Calls"
              subtitle="Calls with the highest scores"
              data={metrics.topCalls}
              onCallClick={onCallClick}
            />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <PerformanceTable
              title="Bottom 5 Calls"
              subtitle="Calls with the lowest scores"
              data={metrics.bottomCalls}
              onCallClick={onCallClick}
            />
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">{`Performance of ${SAMPLE_CALLS_COUNT} Calls`}</Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ToggleButtonGroup
              value={callsMode}
              exclusive
              onChange={handleCallsModeChange}
              size="small"
            >
              <ToggleButton value="recent">
                {`LAST ${SAMPLE_CALLS_COUNT}`}
              </ToggleButton>
              <ToggleButton value="random">
                {`RANDOM ${SAMPLE_CALLS_COUNT}`}
              </ToggleButton>
            </ToggleButtonGroup>
            {callsMode === "random" && (
              <IconButton
                onClick={refreshRandomCalls}
                size="small"
                sx={{ ml: 1 }}
                aria-label="Refresh random calls"
              >
                <RefreshIcon />
              </IconButton>
            )}
          </Box>
        </Box>
        <PerformanceTable
          data={callsMode === "recent" ? metrics.recentCalls : metrics.randomCalls}
          onCallClick={onCallClick}
        />
      </Paper>
    </Box>
  );
};

export default CallSamplesSection;
